/**
 * What a select field offers, and which of those a new row starts with.
 *
 * The options are the field: everything a cell of one can hold is in this list,
 * so adding, renaming, recolouring, reordering and removing them is the whole of
 * the configuration. Their *order* is not decoration either -- it is the order
 * every dropdown over this field shows them in, and the backend takes it from
 * the order they are sent in.
 *
 * An option that does not exist yet carries a negative id. The backend has never
 * issued one, so it reads as "create this", and the same number named as the
 * field's default is resolved to the real option once it has one -- which is
 * what lets an option be made the default in the request that creates it.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GripVertical, X } from 'lucide-react'
import { verticalListSortingStrategy } from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'

import type { SelectOption } from '@/api/types'
import { SortableList } from '@/shared/components/sortable'
import { useSortableItem } from '@/shared/hooks/use-sortable-item'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { nextOptionId, nextUnusedColor, selectColorStyle } from '../../select-options'
import { ColourPicker } from './controls'
import type { SubForm } from './types'

function OptionList({
  options,
  onChange,
}: {
  options: readonly SelectOption[]
  onChange: (options: SelectOption[]) => void
}) {
  const { t } = useTranslation()
  // The option just added, so the cursor lands in its box. Held rather than
  // computed: "the last one" is not it -- an option can be added above nothing
  // and then dragged.
  const [added, setAdded] = useState<number | null>(null)

  const replace = (at: number, option: SelectOption): void => {
    onChange(options.map((held, index) => (index === at ? option : held)))
  }

  const keys = options.map(keyOf)

  return (
    <div className="flex flex-col gap-2">
      <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto">
        <SortableList
          ids={keys}
          strategy={verticalListSortingStrategy}
          modifiers={[restrictToVerticalAxis]}
          onReorder={(next) => {
            const at = new Map(next.map((id, index) => [id, index]))
            onChange(
              options
                .map((option, index) => ({ option, key: keys[index] ?? 0 }))
                .sort((a, b) => (at.get(a.key) ?? 0) - (at.get(b.key) ?? 0))
                .map(({ option }) => option),
            )
          }}
        >
          {options.map((option, index) => (
            <OptionRow
              key={keys[index]}
              option={option}
              autoFocus={option.id === added}
              onChange={(next) => {
                replace(index, next)
              }}
              onRemove={() => {
                onChange(options.filter((_unused, at) => at !== index))
              }}
            />
          ))}
        </SortableList>
      </ul>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        onClick={() => {
          const id = nextOptionId(options)
          setAdded(id)
          onChange([
            ...options,
            {
              id,
              value: '',
              // Palette order rather than at random, so a run of options comes
              // out distinct and the same way twice.
              color: nextUnusedColor(options.map((option) => option.color)),
            },
          ])
        }}
      >
        {t('fieldConfig.addOption')}
      </Button>
    </div>
  )
}

function OptionRow({
  option,
  autoFocus,
  onChange,
  onRemove,
}: {
  option: SelectOption
  autoFocus: boolean
  onChange: (option: SelectOption) => void
  onRemove: () => void
}) {
  const { t } = useTranslation()
  const { setNodeRef, style, handleProps, isDragging } = useSortableItem(option.id ?? 0)
  const named = option.value === '' ? t('fieldConfig.optionUnnamed') : option.value

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1 ${isDragging ? 'opacity-60' : ''}`}
    >
      <button
        type="button"
        aria-label={t('fieldConfig.optionMove', { option: named })}
        className="cursor-grab rounded p-0.5 text-muted-foreground hover:bg-muted"
        {...handleProps}
      >
        <GripVertical className="size-4" />
      </button>

      <ColourPicker
        label={t('fieldConfig.optionColour', { option: named })}
        colour={option.color}
        onChange={(color) => {
          onChange({ ...option, color })
        }}
      />

      <Input
        autoFocus={autoFocus}
        className="h-8 flex-1"
        aria-label={t('fieldConfig.optionName')}
        value={option.value}
        onChange={(event) => {
          onChange({ ...option, value: event.target.value })
        }}
      />

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-7 shrink-0"
        aria-label={t('fieldConfig.optionRemove', { option: named })}
        onClick={onRemove}
      >
        <X className="size-4" />
      </Button>
    </li>
  )
}

/**
 * The number one option is tracked by while the dialog is open.
 *
 * Every option this dialog holds has an id -- the server issues one, or the
 * list gives a new one a negative number -- so the fallback is for the schema's
 * sake rather than for a case that happens.
 */
const keyOf = (option: SelectOption, index: number): number => option.id ?? -1 - index

export const SingleSelectForm: SubForm<'single_select'> = ({ properties, onChange }) => {
  const { t } = useTranslation()
  const id = useId()
  const options = properties.select_options ?? []
  const chosen = properties.single_select_default ?? null

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label>{t('fieldConfig.options')}</Label>
        <OptionList
          options={options}
          onChange={(select_options) => {
            // A default whose option has just been removed is not a default any
            // more. Left behind, it points at a row nothing can select.
            const kept = select_options.some((option) => option.id === chosen)
            onChange({
              select_options,
              ...(chosen !== null && !kept ? { single_select_default: null } : {}),
            })
          }}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={id}>{t('fieldConfig.defaultOption')}</Label>
        <NativeSelect
          id={id}
          className="w-full"
          value={chosen === null ? '' : String(chosen)}
          onChange={(event) => {
            onChange({
              single_select_default:
                event.target.value === '' ? null : Number(event.target.value),
            })
          }}
        >
          <NativeSelectOption value="">{t('fieldConfig.optionNone')}</NativeSelectOption>
          {options.map((option) => (
            <NativeSelectOption key={option.id} value={option.id}>
              {option.value === '' ? t('fieldConfig.optionUnnamed') : option.value}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </div>
    </>
  )
}

export const MultipleSelectForm: SubForm<'multiple_select'> = ({
  properties,
  onChange,
}) => {
  const { t } = useTranslation()
  const options = properties.select_options ?? []
  const chosen = properties.multiple_select_default ?? []

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label>{t('fieldConfig.options')}</Label>
        <OptionList
          options={options}
          onChange={(select_options) => {
            const ids = new Set(select_options.map((option) => option.id))
            onChange({
              select_options,
              multiple_select_default: chosen.filter((id) => ids.has(id)),
            })
          }}
        />
      </div>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="mb-1.5 text-sm font-medium">
          {t('fieldConfig.defaultOptions')}
        </legend>
        {options.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('fieldConfig.optionNone')}</p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {options.map((option) => (
              <DefaultChip
                key={option.id}
                option={option}
                chosen={chosen.includes(option.id ?? 0)}
                onToggle={(on) => {
                  const id = option.id ?? 0
                  onChange({
                    multiple_select_default: on
                      ? [...chosen, id]
                      : chosen.filter((held) => held !== id),
                  })
                }}
              />
            ))}
          </div>
        )}
      </fieldset>
    </>
  )
}

function DefaultChip({
  option,
  chosen,
  onToggle,
}: {
  option: SelectOption
  chosen: boolean
  onToggle: (chosen: boolean) => void
}) {
  const { t } = useTranslation()
  const style = selectColorStyle(option.color)

  return (
    <button
      type="button"
      aria-pressed={chosen}
      // The colour is on the chip whether or not it is chosen, because it is
      // what the option *is*; being chosen is said with the ring, which does not
      // have to compete with forty-two backgrounds to be visible.
      className={`rounded px-2 py-0.5 text-xs ${chosen ? 'ring-2 ring-ring ring-offset-1' : 'opacity-60'}`}
      style={style}
      onClick={() => {
        onToggle(!chosen)
      }}
    >
      {option.value === '' ? t('fieldConfig.optionUnnamed') : option.value}
    </button>
  )
}
